import { useEffect, useRef } from "react";

interface WaveformTrackProps {
  analyser: AnalyserNode | null;
  label: string;
  height?: number;
  color?: string;
  windowSec?: number;
}

/**
 * Scrolling waveform canvas — renders peak amplitude from an AnalyserNode
 * as filled vertical bars (like an audio editor).
 * Amplitude is displayed in dB scale for better visibility of quiet signals.
 */
export default function WaveformTrack({
  analyser,
  label,
  height = 48,
  color = "#22d3ee",
  windowSec = 5,
}: WaveformTrackProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const bufRef = useRef<Float32Array<ArrayBuffer> | null>(null);
  const historyRef = useRef<number[]>([]);
  const rafRef = useRef<number>(0);
  const lastDrawRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const FRAME_INTERVAL = 33; // ~30fps

    /** Convert linear amplitude [0,1] → normalized display height [0,1].
     *  Uses dB scale with floor at -60dB so quiet signals are still visible. */
    const ampToDisplay = (amp: number): number => {
      if (amp <= 0) return 0;
      const db = 20 * Math.log10(amp);
      // Map [-60dB, 0dB] → [0, 1]
      const normalized = (db + 60) / 60;
      return Math.max(0, Math.min(1, normalized));
    };

    const draw = (now: number) => {
      rafRef.current = requestAnimationFrame(draw);

      if (now - lastDrawRef.current < FRAME_INTERVAL) return;
      lastDrawRef.current = now;

      // Ensure canvas pixel dimensions match CSS layout
      const rect = canvas.getBoundingClientRect();
      const dw = Math.floor(rect.width);
      const dh = Math.floor(rect.height);
      if (dw < 1 || dh < 1) return; // not laid out yet
      if (canvas.width !== dw || canvas.height !== dh) {
        canvas.width = dw;
        canvas.height = dh;
      }

      const w = canvas.width;
      const h = canvas.height;

      // Sample the analyser for peak amplitude
      let peak = 0;
      if (analyser) {
        if (!bufRef.current || bufRef.current.length !== analyser.fftSize) {
          bufRef.current = new Float32Array(analyser.fftSize);
        }
        analyser.getFloatTimeDomainData(bufRef.current);
        const buf = bufRef.current;
        for (let i = 0; i < buf.length; i++) {
          const abs = Math.abs(buf[i]!);
          if (abs > peak) peak = abs;
        }
      }

      // Push to history (cap to canvas width)
      const hist = historyRef.current;
      hist.push(peak);
      if (hist.length > w) {
        hist.splice(0, hist.length - w);
      }

      // Clear
      ctx.fillStyle = "#0a0a0a";
      ctx.fillRect(0, 0, w, h);

      // Center line
      const cy = h / 2;
      ctx.fillStyle = "#1a1a1a";
      ctx.fillRect(0, cy, w, 1);

      // Draw waveform bars (filled rectangles, symmetric around center)
      ctx.fillStyle = color;
      const startX = w - hist.length;
      for (let i = 0; i < hist.length; i++) {
        const x = startX + i;
        const displayAmp = ampToDisplay(hist[i] ?? 0);
        if (displayAmp <= 0) continue;
        // Minimum 1px bar height when there's any signal
        const barH = Math.max(1, displayAmp * (h * 0.45));
        ctx.fillRect(x, cy - barH, 1, barH * 2);
      }

      // Label (top-left)
      ctx.fillStyle = "#666";
      ctx.font = "10px monospace";
      ctx.fillText(label, 4, 11);

      // Peak dB indicator (top-right)
      const db = peak > 0 ? (20 * Math.log10(peak)).toFixed(1) : "-inf";
      ctx.fillStyle = peak > 0.01 ? "#4ade80" : "#555";
      ctx.fillText(`${db} dB`, w - 65, 11);
    };

    void windowSec;
    rafRef.current = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(rafRef.current);
    };
  }, [analyser, color, label, windowSec]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        width: "100%",
        height,
        display: "block",
        borderRadius: 3,
        background: "#0a0a0a",
      }}
    />
  );
}
